import {
  AlertOctagon,
  AlertTriangle,
  Bell,
  Info,
  Megaphone,
  TriangleAlert,
  type LucideIcon,
} from 'lucide-react'
import type { NotificationKind } from '@/services/notifications/types'

export interface KindStyle {
  label: string
  icon: LucideIcon
  iconClass: string
  borderClass: string
  /** Fundo translúcido — listas inline (popover, página). */
  bgClass: string
  /** Fundo opaco — toast sobreposto precisa esconder o conteúdo de trás. */
  toastBgClass: string
  badgeClass: string
}

export const KIND_STYLES: Record<NotificationKind, KindStyle> = {
  info: {
    label: 'Info',
    icon: Info,
    iconClass: 'text-sky-600 dark:text-sky-400',
    borderClass: 'border-sky-200 dark:border-sky-900',
    bgClass: 'bg-sky-50/50 dark:bg-sky-950/20',
    toastBgClass: 'bg-sky-50 dark:bg-sky-950',
    badgeClass: 'bg-sky-100 text-sky-800 dark:bg-sky-900/40 dark:text-sky-200',
  },
  notify: {
    label: 'Notificação',
    icon: Bell,
    iconClass: 'text-blue-600 dark:text-blue-400',
    borderClass: 'border-blue-200 dark:border-blue-900',
    bgClass: 'bg-blue-50/50 dark:bg-blue-950/20',
    toastBgClass: 'bg-blue-50 dark:bg-blue-950',
    badgeClass: 'bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-200',
  },
  marketing: {
    label: 'Novidade',
    icon: Megaphone,
    iconClass: 'text-emerald-600 dark:text-emerald-400',
    borderClass: 'border-emerald-200 dark:border-emerald-900',
    bgClass: 'bg-emerald-50/50 dark:bg-emerald-950/20',
    toastBgClass: 'bg-emerald-50 dark:bg-emerald-950',
    badgeClass: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-200',
  },
  warning: {
    label: 'Atenção',
    icon: TriangleAlert,
    iconClass: 'text-amber-600 dark:text-amber-400',
    borderClass: 'border-amber-200 dark:border-amber-900',
    bgClass: 'bg-amber-50/50 dark:bg-amber-950/20',
    toastBgClass: 'bg-amber-50 dark:bg-amber-950',
    badgeClass: 'bg-amber-100 text-amber-900 dark:bg-amber-900/40 dark:text-amber-200',
  },
  alert: {
    label: 'Alerta',
    icon: AlertTriangle,
    iconClass: 'text-orange-600 dark:text-orange-400',
    borderClass: 'border-orange-300 dark:border-orange-900',
    bgClass: 'bg-orange-50/60 dark:bg-orange-950/20',
    toastBgClass: 'bg-orange-50 dark:bg-orange-950',
    badgeClass: 'bg-orange-100 text-orange-900 dark:bg-orange-900/40 dark:text-orange-200',
  },
  critical: {
    label: 'Crítico',
    icon: AlertOctagon,
    iconClass: 'text-red-600 dark:text-red-400',
    borderClass: 'border-red-300 dark:border-red-900',
    bgClass: 'bg-red-50/60 dark:bg-red-950/20',
    toastBgClass: 'bg-red-50 dark:bg-red-950',
    badgeClass: 'bg-red-100 text-red-900 dark:bg-red-900/40 dark:text-red-200',
  },
}

/** Kind fora do contrato (tipo novo no back) degrada pra info em vez de quebrar a tela. */
export function getKindStyle(kind: NotificationKind): KindStyle {
  return KIND_STYLES[kind] ?? KIND_STYLES.info
}

const KIND_PRIORITY: Record<NotificationKind, number> = {
  critical: 5,
  alert: 4,
  warning: 3,
  notify: 2,
  info: 1,
  marketing: 0,
}

export function getKindPriority(kind: NotificationKind): number {
  return KIND_PRIORITY[kind] ?? 0
}

/** Severidade primeiro, data como desempate (mais nova antes). */
export function compareByPriority(
  a: { kind: NotificationKind; created_at: number },
  b: { kind: NotificationKind; created_at: number },
): number {
  const diff = getKindPriority(b.kind) - getKindPriority(a.kind)
  if (diff !== 0) return diff
  return b.created_at - a.created_at
}
